import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  SaasSettings,
  SaasSettingsUpdatePayload,
  SaasStatusResponse
} from '../models/saas.models';

@Injectable({
  providedIn: 'root'
})
export class SaasService {
  private readonly apiUrl = environment.apiUrl;
  private readonly statusSubject = new BehaviorSubject<SaasSettings | null>(null);
  public readonly status$ = this.statusSubject.asObservable();

  constructor(private http: HttpClient) {}

  public getStatus(force = false): Observable<SaasStatusResponse> {
    if (!force && this.statusSubject.value) {
      return new Observable((subscriber) => {
        subscriber.next({ success: true, data: this.statusSubject.value! });
        subscriber.complete();
      });
    }

    return this.http.get<SaasStatusResponse>(`${this.apiUrl}/saas/status`).pipe(
      tap((response) => {
        if (response.success) {
          this.statusSubject.next(response.data);
        }
      })
    );
  }

  public refreshStatus(): Observable<SaasStatusResponse> {
    return this.getStatus(true);
  }

  public getCachedStatus(): SaasSettings | null {
    return this.statusSubject.value;
  }

  public getSettings(): Observable<SaasStatusResponse> {
    return this.http.get<SaasStatusResponse>(`${this.apiUrl}/saas/settings`).pipe(
      tap((response) => {
        if (response.success) {
          this.statusSubject.next(response.data);
        }
      })
    );
  }

  public updateSettings(payload: SaasSettingsUpdatePayload): Observable<SaasStatusResponse> {
    return this.http.put<SaasStatusResponse>(`${this.apiUrl}/saas/settings`, payload).pipe(
      tap((response) => {
        if (response.success) {
          this.statusSubject.next(response.data);
        }
      })
    );
  }

  public setStatusFromError(mode: 'suspension' | 'maintenance', message: string): void {
    const current = this.statusSubject.value;
    const next: SaasSettings = {
      suspension_alert_enabled: current?.suspension_alert_enabled ?? false,
      suspension_screen_enabled: mode === 'suspension',
      suspension_screen_title: current?.suspension_screen_title || 'Serviço suspenso',
      suspension_alert_message:
        current?.suspension_alert_message || current?.suspension_message || '',
      suspension_screen_message:
        mode === 'suspension'
          ? message
          : (current?.suspension_screen_message || ''),
      suspension_message:
        mode === 'suspension'
          ? message
          : (current?.suspension_message || current?.suspension_alert_message || ''),
      maintenance_alert_enabled: current?.maintenance_alert_enabled ?? false,
      maintenance_screen_enabled: mode === 'maintenance',
      maintenance_screen_title: current?.maintenance_screen_title || 'Sistema em manutenção',
      maintenance_alert_message: current?.maintenance_alert_message || '',
      maintenance_screen_message:
        mode === 'maintenance'
          ? message
          : (current?.maintenance_screen_message || current?.maintenance_message || ''),
      maintenance_message:
        mode === 'maintenance'
          ? message
          : (current?.maintenance_message || current?.maintenance_screen_message || ''),
      screen_mode: mode,
      alert_mode: current?.alert_mode ?? null,
      defaults: current?.defaults
    };
    this.statusSubject.next(next);
  }
}
